import React, { KeyboardEvent } from 'react';

import { ScheduleEntry } from 'us-web-services';
import { useSharedTranslation } from '../hooks/useSharedTranslation';
import {
  MONTHS,
  WEEKDAYS,
  bookingStatusKey,
  formatTime,
  isSameDay,
  parseApiDate,
} from './utils';

interface Props {
  schedule: ScheduleEntry[];
  onSelectDate: (date: Date) => void;
  namespace?: string;
  emptyMessage?: string;
}

const onKeyActivate = (
  handler: () => void,
): ((e: KeyboardEvent<HTMLDivElement>) => void) =>
  (e): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handler();
    }
  };

export const AgendaList = ({
  schedule,
  onSelectDate,
  namespace,
  emptyMessage,
}: Props): JSX.Element => {
  const { t } = useSharedTranslation(namespace);

  const now = new Date();
  const upcoming = schedule
    .filter((e) => {
      const d = parseApiDate(e.start);
      return d ? d.getTime() >= now.getTime() - 60 * 60 * 1000 : false;
    })
    .sort((a, b) => {
      const da = parseApiDate(a.start);
      const db = parseApiDate(b.start);
      if (!da || !db) return 0;
      return da.getTime() - db.getTime();
    });

  if (upcoming.length === 0) {
    return (
      <div className='bg-white rounded p-3 text-sm text-neutral-600'>
        {emptyMessage ?? t('pages.calendar.noUpcoming')}
      </div>
    );
  }

  const allGroups: Array<{ date: Date; items: ScheduleEntry[] }> = [];
  for (const entry of upcoming) {
    const d = parseApiDate(entry.start);
    if (!d) continue;
    const last = allGroups[allGroups.length - 1];
    if (last && isSameDay(last.date, d)) {
      last.items.push(entry);
    } else {
      allGroups.push({ date: d, items: [entry] });
    }
  }

  return (
    <div className='bg-white rounded flex flex-col max-h-[28rem]'>
      <h5 className='px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-neutral-500'>
        {t('pages.calendar.upcoming')}
      </h5>
      <ul className='overflow-y-auto flex-1'>
        {allGroups.map((group) => (
          <li key={group.date.toISOString()}>
            <div
              role='button'
              tabIndex={0}
              onClick={(): void => onSelectDate(group.date)}
              onKeyDown={onKeyActivate(() => onSelectDate(group.date))}
              className='cursor-pointer px-3 py-2 border-b border-neutral-100 last:border-b-0 hover:bg-sapphire-50 transition-colors'
            >
              <div className='text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-1'>
                {WEEKDAYS[group.date.getDay()]} {MONTHS[group.date.getMonth()].slice(0, 3)} {group.date.getDate()}
              </div>
              {group.items.map((item) => {
                if (item.booking) {
                  const parent = item.booking.parent;
                  const firstName = parent?.firstName ?? parent?.name ?? t('pages.calendar.booking');
                  const lastInitial = parent?.lastName ? `${parent.lastName.charAt(0)}.` : '';
                  const city = item.booking.address?.city;
                  const state = item.booking.address?.state;
                  return (
                    <div
                      key={item.id ?? item.start}
                      className='flex items-start gap-2 mt-1'
                    >
                      {parent?.pictureUrl && (
                        <img
                          alt=''
                          src={parent.pictureUrl}
                          className='w-8 h-8 rounded-full object-cover flex-shrink-0'
                        />
                      )}
                      <div className='text-xs text-neutral-700 leading-tight'>
                        <div className='font-semibold text-sapphire'>
                          {t('common.timeRange', { start: formatTime(item.start), end: item.pastMidnight ? t('pages.calendar.midnightPlus') : formatTime(item.end) })}
                        </div>
                        <div className='font-semibold'>
                          {t(bookingStatusKey(item.booking))}
                        </div>
                        <div>
                          {firstName} {lastInitial}
                        </div>
                        {city && state && (
                          <div className='text-neutral-500'>
                            {city}, {state}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                }
                return (
                  <div
                    key={item.id ?? item.start}
                    className='text-xs text-emerald-700 mt-1'
                  >
                    <span className='font-semibold'>{t('pages.calendar.available')}</span>
                    {' '}
                    <span className='text-neutral-500'>
                      {t('common.timeRange', { start: formatTime(item.start), end: item.pastMidnight ? t('pages.calendar.midnightPlus') : formatTime(item.end) })}
                    </span>
                  </div>
                );
              })}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};
